import { memo } from "react";

import {
  FieldDefinitionAddressType,
  FieldDefinitionBooleanType,
  FieldDefinitionButtonType,
  FieldDefinitionCurrencyType,
  FieldDefinitionCustomType,
  FieldDefinitionDateType,
  FieldDefinitionEnumsType,
  FieldDefinitionEnumType,
  FieldDefinitionFieldType,
  FieldDefinitionListType,
  FieldDefinitionMetricType,
  FieldDefinitionPaginatedTableType,
  FieldDefinitionPathType,
  FieldDefinitionPercentType,
  FieldDefinitionPicturesType,
  FieldDefinitionReferencesType,
  FieldDefinitionReferenceType,
  FieldDefinitionRouterType,
  FieldDefinitionSelectType,
  FieldDefinitionTableType,
  FieldDefinitionTagsType,
  FieldDefinitionTagType,
  FieldDefinitionTextType,
  FieldDefinitionType,
  FieldDefinitionUrlType,
  FieldEventsType,
  FieldImperativeHandle,
  FieldPaginatedTableValueOptions,
  FieldValueType,
} from "@/components/@types";
import { Address } from "@/components/organisms/Field/FieldAddress";
import { FieldBoolean } from "@/components/organisms/Field/FieldBoolean";
import { FieldButton } from "@/components/organisms/Field/FieldButton";
import { FieldCustom } from "@/components/organisms/Field/FieldCustom";
import { FieldDate } from "@/components/organisms/Field/FieldDate";
import { Enums, FieldEnum } from "@/components/organisms/Field/FieldEnum";
import { FieldList } from "@/components/organisms/Field/FieldList";
import { FieldMetric } from "@/components/organisms/Field/FieldMetric";
import { FieldPath } from "@/components/organisms/Field/FieldPath";
import { FieldPaginatedTable } from "@/components/organisms/Field/FieldPaginatedTable";
import { FieldPictures } from "@/components/organisms/Field/FieldPictures";
import {
  FieldReference,
  FieldReferences,
} from "@/components/organisms/Field/FieldReferences";
import { FieldRouter } from "@/components/organisms/Field/FieldRouter";
import { FieldSelect } from "@/components/organisms/Field/FieldSelect";
import { FieldTable } from "@/components/organisms/Field/FieldTable";
import { FieldTag, FieldTags } from "@/components/organisms/Field/FieldTag";
import { FieldTypography } from "@/components/organisms/Field/FieldTypography";
import { FieldUrl } from "@/components/organisms/Field/FieldUrl";
import { Utils } from "@/utils";

function InnerField({
  data,
  events,
  type,
  getValue,
  getValueOptions,
  store,
  isMultiline,
  isExpanded,
  size = "md",
  fieldVariant = "node",
  style,
  isLoading,
  ref,
}: {
  data: FieldValueType;
  events: FieldEventsType;
  type: FieldDefinitionType["type"];
  getValue: FieldDefinitionType["getValue"];
  getValueOptions?: FieldDefinitionType["getValueOptions"];
  isMultiline?: FieldDefinitionFieldType<FieldDefinitionType>["isMultiline"];
  isExpanded?: boolean;
  size?: FieldDefinitionFieldType<FieldDefinitionType>["size"];
  store?: FieldDefinitionFieldType<FieldDefinitionType>["store"];
  fieldVariant?: FieldDefinitionFieldType<FieldDefinitionType>["fieldVariant"];
  style?: React.CSSProperties;
  isLoading?: boolean;
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const value = getValue(data);
  const valueOptions = getValueOptions?.(data);

  const props = {
    value,
    events,
    valueOptions,
    isMultiline,
    size,
    fieldVariant,
    style,
    store,
    isLoading,
    ref,
  };

  switch (type) {
    case "address":
      return (
        <Address
          {...(props as FieldDefinitionFieldType<FieldDefinitionAddressType>)}
          ref={ref}
        />
      );
    case "boolean":
      return (
        <FieldBoolean
          {...(props as FieldDefinitionFieldType<FieldDefinitionBooleanType>)}
          ref={ref}
        />
      );
    case "button":
      return (
        <FieldButton
          {...(props as FieldDefinitionFieldType<FieldDefinitionButtonType>)}
          ref={ref}
        />
      );
    case "currency":
      return (
        <FieldTypography
          {...(props as FieldDefinitionFieldType<FieldDefinitionCurrencyType>)}
          mode="currency"
          ref={ref}
        />
      );
    case "custom":
      return (
        <FieldCustom
          {...(props as FieldDefinitionFieldType<FieldDefinitionCustomType>)}
          ref={ref}
        />
      );
    case "date":
      // Editable, it is picked from a calendar; read, it is the day written
      // out - and nothing at all when there is none.
      return props.events?.onChangeField ? (
        <FieldDate
          {...(props as FieldDefinitionFieldType<FieldDefinitionDateType>)}
          value={typeof value === "string" ? value : ""}
          ref={ref}
        />
      ) : (
        <FieldTypography
          {...(props as FieldDefinitionFieldType<FieldDefinitionDateType>)}
          value={value ? Utils.fDate(new Date(value as string)) : ""}
          mode="date"
          ref={ref}
        />
      );
    // case "datetime":
    //   return null;
    // case "email":
    //   return null;
    case "enum":
      return (
        <FieldEnum
          {...(props as FieldDefinitionFieldType<FieldDefinitionEnumType>)}
          ref={ref}
        />
      );
    case "enums":
      return (
        <Enums
          {...(props as FieldDefinitionFieldType<FieldDefinitionEnumsType>)}
          ref={ref}
        />
      );
    // case "image":
    //   return null;
    case "list":
      return (
        <FieldList
          {...(props as FieldDefinitionFieldType<FieldDefinitionListType>)}
          ref={ref}
        />
      );
    case "metric":
      return (
        <FieldMetric
          {...(props as FieldDefinitionFieldType<FieldDefinitionMetricType>)}
          ref={ref}
        />
      );
    case "number":
      return (
        <FieldTypography
          {...(props as FieldDefinitionFieldType<FieldDefinitionTextType>)} // FieldUnionNumberType
          mode="number"
          ref={ref}
        />
      );
    case "path":
      return (
        <FieldPath
          {...(props as FieldDefinitionFieldType<FieldDefinitionPathType>)}
          ref={ref}
        />
      );
    case "percent":
      return (
        <FieldTypography
          {...(props as FieldDefinitionFieldType<FieldDefinitionPercentType>)}
          mode="percent"
          ref={ref}
        />
      );
    // case "phone":
    //   return null;
    case "pictures":
      return (
        <FieldPictures
          {...(props as FieldDefinitionFieldType<FieldDefinitionPicturesType>)}
        />
      );
    case "router":
      return (
        <FieldRouter
          data={data}
          {...(props as FieldDefinitionFieldType<FieldDefinitionRouterType>)}
          ref={ref}
        />
      );
    case "reference":
      return (
        <FieldReference
          {...(props as FieldDefinitionFieldType<FieldDefinitionReferenceType>)}
          ref={ref}
        />
      );
    case "select":
      return (
        <FieldSelect
          {...(props as FieldDefinitionFieldType<FieldDefinitionSelectType>)}
          ref={ref}
        />
      );
    case "references":
      return (
        <FieldReferences
          {...(props as FieldDefinitionFieldType<FieldDefinitionReferencesType>)}
          ref={ref}
        />
      );
    // case "string":
    //   return null;
    case "paginatedTable":
      return (
        <FieldPaginatedTable
          data={data}
          valueOptions={(valueOptions ?? {}) as FieldPaginatedTableValueOptions}
          style={style}
        />
      );
    case "table":
      return (
        <FieldTable
          {...(props as FieldDefinitionFieldType<FieldDefinitionTableType>)}
          ref={ref}
        />
      );
    case "tag":
      return (
        <FieldTag
          {...(props as FieldDefinitionFieldType<FieldDefinitionTagType>)}
          ref={ref}
        />
      );
    case "tags":
      return (
        <FieldTags
          {...(props as FieldDefinitionFieldType<FieldDefinitionTagsType>)}
          ref={ref}
        />
      );
    case "text":
      return (
        <FieldTypography
          {...(props as FieldDefinitionFieldType<FieldDefinitionTextType>)}
          isExpanded={isExpanded}
          ref={ref}
        />
      );
    // case "time":
    //   return null;
    case "url":
      return (
        <FieldUrl
          {...(props as FieldDefinitionFieldType<FieldDefinitionUrlType>)}
          ref={ref}
        />
      );
    default:
      return (
        <FieldTypography
          {...(props as FieldDefinitionFieldType<FieldDefinitionTextType>)}
          isExpanded={isExpanded}
          ref={ref}
        />
      );
  }
}

// Memoize the component
export const Field = memo(InnerField, (prevProps, nextProps) => {
  return (
    prevProps.data === nextProps.data &&
    prevProps.isMultiline === nextProps.isMultiline &&
    prevProps.isExpanded === nextProps.isExpanded &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.events === nextProps.events
  );
});

export * from "./FieldAddress";
export * from "./FieldBoolean";
export * from "./FieldButton";
export * from "./FieldCell";
export * from "./FieldCustom";
export * from "./FieldEnum";
export * from "./FieldList";
export * from "./FieldPaginatedTable";
export * from "./FieldPath";
export * from "./FieldReferences";
export * from "./FieldRouter";
export * from "./FieldSelect";
export * from "./FieldTable";
export * from "./FieldTag";
export * from "./FieldTypography";
export * from "./FieldUrl";
